<template>
  <div class="products">
    <!-- 顶部标题区域 -->
    <section class="products-header">
      <div class="container">
        <h1 class="page-title">
          <span class="title-glow">产品中心</span>
        </h1>
        <p class="page-subtitle">精选产品与解决方案</p>
      </div>
    </section>

    <!-- 主内容区域 -->
    <section class="products-main">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="loading" class="products-loading">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>
        <!-- 错误提示 -->
        <div v-else-if="error" class="products-error">
          <p>{{ error }}</p>
          <p class="error-hint">请确保 Node.js 服务器正在运行（端口 3000）</p>
        </div>
        <!-- 产品内容 -->
        <div v-else class="products-layout">
          <!-- 左侧导航栏 -->
          <aside class="products-sidebar">
            <div 
              v-for="(system, index) in productSystems" 
              :key="index"
              :id="getSystemId(index)"
              class="sidebar-item"
              :class="{ active: activeSystem === index }"
              @click="handleSystemClick(index)"
            >
              <div class="sidebar-icon" aria-hidden="true">
                <i class="iconfont" :class="getSystemIcon(system, index)"></i>
              </div>
              <div class="sidebar-content">
                <h3 class="sidebar-title">{{ system.name }}</h3>
                <p class="sidebar-desc">{{ system.description }}</p>
              </div>
              <div class="sidebar-indicator" v-if="activeSystem === index"></div>
            </div>
          </aside>

          <!-- 右侧内容区域 -->
          <main class="products-content">
            <div 
              v-for="(system, index) in productSystems" 
              :key="index"
              :id="getSystemId(index)"
              class="content-section"
              :class="{ active: activeSystem === index }"
            >
              <h2 class="content-title">{{ system.name }}</h2>
              <!-- <p class="content-subtitle">{{ system.description }}</p> -->
              <p class="content-description">{{ system.fullDescription }}</p>
              
              <div class="products-grid">
                <div 
                  v-for="(product, pIndex) in system.products" 
                  :key="pIndex"
                  class="product-card"
                  @click="goToProductDetail(product)"
                >
                  <div class="product-image">
                    <img :src="resolveProductImage(product.image)" :alt="product.title" v-if="product.image" />
                    <div class="product-image-placeholder" v-else>
                      <div class="placeholder-icon">{{ product.icon }}</div>
                    </div>
                  </div>
                  <div class="product-info">
                    <h3 class="product-title">{{ product.title }}</h3>
                    <p class="product-desc">{{ product.description }}</p>
                    <button
                      type="button"
                      class="product-link"
                      @click.stop="goToProductDetail(product)"
                    >
                      了解更多
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

// 使用 require.context 让 webpack 在打包时收集产品中心卡片图片资源
const productImageCtx = require.context('@/assets/products/img_home', false, /\.(png|jpe?g)$/)

const DEFAULT_SYSTEM_ICONS = {
  'big-data-platform': 'icon-dashuju',
  'application-center': 'icon-kuapingtaiyingyong',
  'operation-center': 'icon-tongxinguanli'
}

export default {
  name: 'Products',
  data() {
    return {
      activeSystem: 0, // 默认选中第一个：大数据平台
      productSystems: [],
      loading: false,
      error: null
    }
  },
  watch: {
    '$route.hash'() {
      // 监听路由 hash 变化，支持通过 URL hash 跳转到指定系统
      this.syncSystemWithHash();
    },
    '$route.path'(newPath, oldPath) {
      // 当从其他页面返回到产品中心时，重新加载数据
      if (newPath === '/products' && oldPath !== '/products') {
        this.fetchProductSystems().then(() => {
          this.syncSystemWithHash();
        });
      }
    }
  },
  async mounted() {
    await this.fetchProductSystems()
    // 支持通过 URL hash 跳转到指定系统
    this.syncSystemWithHash();
  },
  activated() {
    // 当组件被激活时（从详情页返回），确保数据已加载并同步hash
    if (this.productSystems.length === 0) {
      this.fetchProductSystems().then(() => {
        this.syncSystemWithHash();
      });
    } else {
      this.syncSystemWithHash();
    }
  },
  methods: {
    async fetchProductSystems() {
      try {
        this.loading = true
        this.error = null
        const response = await axios.get('/api/products/systems')
        if (response.data.code === 200) {
          this.productSystems = Array.isArray(response.data.data) ? response.data.data : []
        } else {
          this.error = response.data.message || '获取产品系统列表失败'
          this.productSystems = []
        }
      } catch (error) {
        console.error('获取产品系统列表失败:', error)
        this.error = '网络请求失败，请检查服务器是否运行'
        this.productSystems = []
      } finally {
        this.loading = false
      }
    },
    handleSystemClick(index) {
      this.activeSystem = index
      // 切换系统后，更新 hash 并滚动到合适位置
      const systemId = this.getSystemId(index)
      const normalizedHash = `#${systemId}`
      if (this.$route.hash !== normalizedHash) {
        this.$router.replace({ hash: normalizedHash })
      }
      this.$nextTick(() => {
        this.scrollToProductsMain()
      })
    },
    getSystemIcon(system, index) {
      const iconFromData = system && typeof system.icon === 'string' ? system.icon : ''
      if (iconFromData.startsWith('icon-')) {
        return iconFromData
      }
      const id = system && system.id ? system.id : ''
      if (id && DEFAULT_SYSTEM_ICONS[id]) {
        return DEFAULT_SYSTEM_ICONS[id]
      }
      const fallbackIcons = Object.values(DEFAULT_SYSTEM_ICONS)
      return fallbackIcons[index] || 'icon-yunpingtaiguanli'
    },
    getSystemId(index) {
      const system = this.productSystems[index];
      if (system && system.id) {
        return system.id;
      }
      const ids = ['big-data-platform', 'application-center', 'operation-center'];
      return ids[index] || '';
    },
    getActiveSectionId() {
      const activeSystem = this.productSystems[this.activeSystem];
      if (activeSystem && activeSystem.id) {
        return activeSystem.id;
      }
      return this.getSystemId(this.activeSystem) || 'big-data-platform';
    },
    syncSystemWithHash() {
      const hash = this.$route.hash;
      // 确保 productSystems 已加载
      if (this.productSystems.length === 0) {
        return;
      }
      // 根据 hash 查找对应的系统索引
      const hashValue = hash.replace('#', '');
      if (!hashValue) {
        // 如果没有 hash，默认显示第一个系统
        this.activeSystem = 0;
        return;
      }
      
      const systemIndex = this.productSystems.findIndex(system => {
        const systemId = system.id || '';
        const systemName = system.name || '';
        return systemId === hashValue || 
               systemName === hashValue ||
               // 兼容旧的 hash 格式
               (hashValue === 'big-data-platform' && systemId === 'big-data-platform') ||
               (hashValue === 'application-center' && systemId === 'application-center') ||
               (hashValue === 'operation-center' && systemId === 'operation-center') ||
               (hashValue === '大数据平台' && systemName === '大数据平台') ||
               (hashValue === '应用中心' && systemName === '应用中心') ||
               (hashValue === '运营中心' && systemName === '运营中心');
      });
      
      if (systemIndex !== -1) {
        this.activeSystem = systemIndex;
        // 只有在通过 hash 跳转时再滚动到 products-main 位置
        this.$nextTick(() => {
          this.scrollToProductsMain()
        });
      }
    },
    scrollToProductsMain() {
      const productsMain = document.querySelector('.products-main')
      if (productsMain) {
        const offsetTop = productsMain.offsetTop - 70 // 减去固定导航栏高度
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        })
      }
    },
    goToProductDetail(product) {
      if (product && product.id) {
        this.$router.push({
          path: '/products/detail',
          query: {
            id: product.id,
            section: this.getActiveSectionId()
          }
        })
      } else {
        console.warn('产品缺少ID，无法跳转到详情页', product)
      }
    },
    resolveProductImage(imagePath) {
      if (!imagePath) return ''
      // JSON 中配置的是 "@img_home/filename"
      if (imagePath.startsWith('@img_home/')) {
        const fileName = imagePath.replace('@img_home/', '')
        try {
          return productImageCtx('./' + fileName)
        } catch (e) {
          console.warn('产品中心图片未找到:', fileName, e)
          return ''
        }
      }
      // 其他情况直接返回原路径（例如外部 URL）
      return imagePath
    }
  }
}
</script>

<style scoped>
.products {
  /* min-height: 100vh; */
  background: #ffffff;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 顶部标题区域 */
.products-header {
  position: relative;
  background-image: 
    url('../../assets/background_header/产品中心.png');
    /* linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1419 100%); */
  background-size: cover, 100%;
  background-position: center, center;
  background-blend-mode: overlay;
  padding: 45px 0;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
}

.products-header .container {
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0;
  padding: 0 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
}

.page-title {
  position: relative;
  z-index: 2;
  margin: 0;
  text-align: left;
  padding: 0;
}

.page-subtitle {
  margin: 8px 0 0 0;
  text-align: left;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.8;
  opacity: 1;
  max-width: 900px;
  /* 从大标题起始位置空两个字开始显示 */
  margin-left: 2em;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(5px);
}

.title-glow {
  display: inline-block;
  font-size: 46px;
  font-weight: 900;
  font-family: 'Arial Black', 'Microsoft YaHei', sans-serif;
  letter-spacing: 5px;
  color: #ffffff;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.6));
  position: relative;
  backdrop-filter: blur(5px);
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% center; }
  50% { background-position: 100% center; }
}

/* 主内容区域 */
.products-main {
  background: #ffffff;
  padding: 40px 0 60px 0;
  background: #f6f8fb;
}

.products-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;
}

/* 左侧导航栏 */
.products-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  padding-right: 20px;
  padding: 15px;
}

.sidebar-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 15px;
  margin-bottom: 10px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.sidebar-item:hover {
  background: #f8f9fa;
}

.sidebar-item.active {
  background: #f0f7ff;
}

.sidebar-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(76, 149, 227, 0.15), rgba(30, 85, 162, 0.15));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2b6cb0;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(76, 149, 227, 0.25);
  transition: background 0.3s ease, color 0.3s ease;
}

.sidebar-item:hover .sidebar-icon,
.sidebar-item.active .sidebar-icon {
  background: linear-gradient(135deg, rgba(76, 149, 227, 0.25), rgba(30, 85, 162, 0.25));
  color: #1e55a2;
}

.sidebar-icon .iconfont {
  font-size: 30px;
  line-height: 1;
}

.sidebar-content {
  flex: 1;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 5px 0;
}

.sidebar-desc {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.sidebar-indicator {
  position: absolute;
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 60%;
  background: linear-gradient(135deg, #4c95e3 0%, #1e55a2 100%);
  border-radius: 2px 0 0 2px;
}

/* 右侧内容区域 */
.products-content {
  flex: 1;
  min-width: 0;
  background: #ffffff;
  padding: 15px;
}

.content-section {
  display: none;
}

.content-section.active {
  display: block;
}

.content-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
}

.content-subtitle {
  text-align: left;
  font-size: 15px;
  color: #666666;
  font-style: italic;
  line-height: 1.8;
  margin: 6px 0 16px 0;
}

.content-description {
  font-size: 16px;
  color: #333;
  line-height: 1.8;
  margin: 0 0 40px 0;
  max-width: 900px;
}

/* 产品网格 */
.products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.product-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-color: #4c95e3;
}

.product-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f8f9fa;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%);
}

.placeholder-icon {
  font-size: 64px;
  opacity: 0.5;
}

.product-info {
  padding: 25px;
}

.product-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.product-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  margin: 0 0 20px 0;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-link {
  display: inline-block;
  font-size: 14px;
  color: #4c95e3;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.product-link:hover {
  color: #1e55a2;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .products-layout {
    gap: 30px;
  }

  .products-sidebar {
    width: 240px;
  }
}

@media (max-width: 992px) {
  .products-layout {
    flex-direction: column;
  }

  .products-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding-right: 0;
    padding-bottom: 20px;
    margin-bottom: 30px;
  }

  .sidebar-item {
    display: flex;
    align-items: center;
  }

  .sidebar-indicator {
    display: none;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .products-header .container {
    padding: 0 40px;
  }
}

@media (max-width: 768px) {
  .products-header {
    padding: 50px 0;
    min-height: 190px;
  }

  .title-glow {
    font-size: 32px;
    letter-spacing: 1.5px;
  }

  .products-header .container {
    padding: 0 24px;
  }

  .page-subtitle {
    margin-left: 1.5em;
  }

  .content-title {
    font-size: 28px;
  }

  .content-description {
    font-size: 15px;
  }

  .product-image {
    height: 180px;
  }

  .product-info {
    padding: 20px;
  }

  .product-title {
    font-size: 18px;
  }
}

/* 加载和错误状态 */
.products-loading,
.products-error {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4c95e3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.products-loading p {
  color: #666;
  font-size: 16px;
}

.products-error {
  background: #ffecec;
  border-radius: 8px;
  padding: 30px;
}

.products-error p {
  color: #c0392b;
  margin: 0 0 10px 0;
  font-size: 16px;
}

.error-hint {
  color: #999 !important;
  font-size: 14px !important;
}
</style>


